// How a round usually messages its customers, and how one customer wants to be messaged (0036; Liam
// 30 Sep 2026: "my brother uses messages not whatsapp"). Keep in step with src/contact.ts.
export const MESSAGE_BY = ['whatsapp', 'text', 'email'] as const
export type MessageBy = (typeof MESSAGE_BY)[number]

// A customer can also say no messages at all. NULL on the customer = the round's usual.
export const CUSTOMER_MESSAGE_BY = ['whatsapp', 'text', 'email', 'none'] as const

// Every round made before 0036 is on WhatsApp, so anything unreadable reads as that.
export function messageByOf(v: unknown): MessageBy {
  return (MESSAGE_BY as readonly unknown[]).includes(v) ? (v as MessageBy) : 'whatsapp'
}

// One plain mailbox: nothing that could add to a mailto: link (?, &, commas, spaces). Same rule as
// src/contact.ts EMAIL_RULE.
export const EMAIL_RULE = /^[A-Za-z0-9._+'-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/

// Your messages (0051, 8 Oct 2026): the messages a business may reword. src/business-settings.ts WORDING holds the
// standard wording and the screen; the two lists are pinned equal by scripts/your-messages.test.mjs.
export const WORDING_KEYS = [
  'rebook_due', 'rebook_six_months', 'rebook_overdue', 'rebook_cold', 'rebook_away', 'rebook_winback', 'rebook_second', 'rebook_final',
  'dates_reply', 'visit_confirmed', 'visit_offer', 'visit_moved', 'visit_missed', 'visit_missed_worker',
  'payment_today', 'chase_1', 'chase_2', 'chase_3', 'paid_thanks',
  'enquiry_first', 'enquiry_second', 'enquiry_final', 'enquiry_last', 'quote_nudge',
  'book_again', 'book_again_regular',
] as const
export const MAX_WORDING_LENGTH = 1500

// The stored column: JSON text of the business's own wording, or NULL when every message is standard.
// The caller turns an error into a 400.
export function wordingColumn(input: unknown): { value: string | null } | { error: string } {
  if (input === null) return { value: null }
  if (!input || typeof input !== 'object' || Array.isArray(input)) return { error: 'message_wording must be an object of messages' }
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(input as Record<string, unknown>)) {
    if (!(WORDING_KEYS as readonly string[]).includes(k)) return { error: `unknown message: ${k}` }
    if (typeof v !== 'string') return { error: `${k} must be text` }
    if (v.length > MAX_WORDING_LENGTH) return { error: `${k} is too long (max ${MAX_WORDING_LENGTH} characters)` }
    if (v.trim()) out[k] = v
  }
  return { value: Object.keys(out).length ? JSON.stringify(out) : null }
}

// What the app reads: the object, or null. A damaged value reads as standard wording rather than breaking sign-in.
export function wordingOf(column: unknown): Record<string, string> | null {
  if (typeof column !== 'string' || !column) return null
  try {
    const parsed = JSON.parse(column) as unknown
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null
    const out: Record<string, string> = {}
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) if ((WORDING_KEYS as readonly string[]).includes(k) && typeof v === 'string') out[k] = v
    return Object.keys(out).length ? out : null
  } catch { return null }
}

// How far ahead to ask (0052, Liam 9 Oct 2026): how many days before a visit or due date Messages lists someone under
// Coming up, and so how far ahead Book a visit offers "Message them". One to four weeks; the standard is one, stored as
// NULL. Keep in step with src/business-settings.ts ASK_AHEAD_CHOICES.
export const ASK_AHEAD_DAYS = [7, 14, 21, 28] as const
export const STANDARD_ASK_AHEAD_DAYS = 7

// What the app reads: always one of the four. Anything unreadable reads as the standard week.
export function askAheadOf(column: unknown): number {
  return (ASK_AHEAD_DAYS as readonly unknown[]).includes(column) ? (column as number) : STANDARD_ASK_AHEAD_DAYS
}
